import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/apiClient';
import './AddLLMModal.css';

const AddLLMModal = ({ show, onClose, providers, onSuccess }) => {
    const [formData, setFormData] = useState({
        provider: '',
        api_key: '',
        base_url: '',
        custom_name: '' // For custom provider
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [testing, setTesting] = useState(false);
    const [allProviders, setAllProviders] = useState([]);

    useEffect(() => {
        // Fetch all available providers (not just added ones) for adding new vendors
        const fetchAllProviders = async () => {
            try {
                // backendUrl handled by apiFetch
                const response = await apiFetch(`/api/llm-settings/providers`, {
                });
                if (response.ok) {
                    const data = await response.json();
                    setAllProviders(data.providers || []);
                }
            } catch (err) {
                console.error('Error fetching all providers:', err);
            }
        };
        
        if (show) {
            fetchAllProviders();
        }
    }, [show]);

    if (!show) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear messages when user changes input
        setError(null);
        setSuccess(null);
    };

    const handleTestAndSave = async () => {
        setError(null);
        setSuccess(null);
        setTesting(true);

        try {
            // backendUrl handled by apiFetch
            
            // Get provider info to determine default model (use allProviders, not just added ones)
            let defaultModel = 'gpt-4o-mini'; // Fallback default
            if (allProviders && allProviders.length > 0) {
                const providerInfo = allProviders.find(p => p.id === formData.provider);
                if (providerInfo && providerInfo.models && providerInfo.models.length > 0) {
                    const firstNonCustomModel = providerInfo.models.find(m => m !== 'custom');
                    if (firstNonCustomModel) {
                        defaultModel = firstNonCustomModel;
                    }
                }
            }
            
            // 1. Test the connection
            const testResponse = await apiFetch(`/api/llm-settings/test`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    provider: formData.provider,
                    api_key: formData.api_key,
                    base_url: formData.base_url || undefined,
                    model_name: defaultModel // Use provider-appropriate model
                })
            });

            const testData = await testResponse.json();

            if (testData.success && testData.valid) {
                // 2. Create vendor (metadata only) - Step 1
                // For custom providers, use "custom:name" format for provider ID
                const providerId = formData.provider === 'custom' && formData.custom_name
                    ? `custom:${formData.custom_name.toLowerCase().replace(/[^a-z0-9]/g, '_')}`
                    : formData.provider;
                
                const createVendorResponse = await apiFetch(`/api/llm-settings/vendors`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    provider: providerId,
                        base_url: formData.base_url || undefined,
                        active: true
                    })
                });

                const createVendorData = await createVendorResponse.json();

                if (!createVendorData.success) {
                    setError(createVendorData.error || 'Failed to create vendor');
                    return;
                }

                // 3. Add and test key - Step 2
                const addKeyResponse = await apiFetch(`/api/llm-settings/vendors/${providerId}/keys/test-and-save`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    key_alias: 'default',
                        api_key: formData.api_key
                    })
                });

                const addKeyData = await addKeyResponse.json();

                if (addKeyData.success) {
                    setSuccess('LLM vendor added successfully!');
                    // Reset form
                    setFormData({
                        provider: '',
                        api_key: '',
                        base_url: '',
                        custom_name: ''
                    });
                    // Call success callback and close modal after a brief delay
                    setTimeout(() => {
                        if (onSuccess) onSuccess();
                        onClose();
                    }, 1500);
                } else {
                    setError(addKeyData.error || 'Failed to add API key');
                }
            } else {
                setError(testData.error || 'API key test failed');
            }
        } catch (err) {
            console.error('Error testing/saving LLM:', err);
            setError('Failed to test or save configuration');
        } finally {
            setTesting(false);
        }
    };

    const getProviderInfo = () => {
        return allProviders.find(p => p.id === formData.provider);
    };

    const requiresBaseUrl = formData.provider === 'openai_compatible' || formData.provider === 'deepseek' || formData.provider === 'groq' || formData.provider === 'custom';
    const requiresCustomName = formData.provider === 'custom';
    const providerInfo = getProviderInfo();

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Add LLM Configuration</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                
                <form onSubmit={(e) => { e.preventDefault(); handleTestAndSave(); }}>
                    {error && (
                        <div className="alert alert-error">{error}</div>
                    )}
                    {success && (
                        <div className="alert alert-success">{success}</div>
                    )}
                    
                    <div className="form-group">
                        <label htmlFor="modal_provider">Provider *</label>
                        <select
                            id="modal_provider"
                            name="provider"
                            value={formData.provider}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select provider</option>
                            {allProviders.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="modal_api_key">API Key *</label>
                        <input
                            type="password"
                            id="modal_api_key"
                            name="api_key"
                            value={formData.api_key}
                            onChange={handleInputChange}
                            placeholder="Enter API key"
                            required
                        />
                        {getProviderInfo()?.requires_api_key === false && (
                            <small className="help-text">Optional for this provider</small>
                        )}
                    </div>

                    {requiresCustomName && (
                        <div className="form-group">
                            <label htmlFor="modal_custom_name">Provider Name *</label>
                            <input
                                type="text"
                                id="modal_custom_name"
                                name="custom_name"
                                value={formData.custom_name}
                                onChange={handleInputChange}
                                placeholder="e.g., My Custom API, Company LLM, etc."
                                required
                            />
                            <small className="help-text">
                                A friendly name for your custom provider
                            </small>
                        </div>
                    )}
                    
                    {requiresBaseUrl && (
                        <div className="form-group">
                            <label htmlFor="modal_base_url">Base URL *</label>
                            <input
                                type="url"
                                id="modal_base_url"
                                name="base_url"
                                value={formData.base_url}
                                onChange={handleInputChange}
                                placeholder={
                                    formData.provider === 'deepseek' 
                                        ? 'https://api.deepseek.com/v1'
                                        : formData.provider === 'groq'
                                        ? 'https://api.groq.com/openai/v1'
                                        : 'http://localhost:11434/v1'
                                }
                                required
                            />
                            <small className="help-text">
                                {formData.provider === 'deepseek' 
                                    ? 'DeepSeek API base URL'
                                    : formData.provider === 'groq'
                                    ? 'Groq API base URL (default: https://api.groq.com/openai/v1)'
                                    : formData.provider === 'custom'
                                    ? 'Base URL for your OpenAI-compatible API'
                                    : 'Base URL for OpenAI-compatible API (e.g., Ollama, vLLM)'}
                            </small>
                        </div>
                    )}

                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn btn-secondary">
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={
                                testing || 
                                !formData.provider || 
                                (providerInfo?.requires_api_key !== false && !formData.api_key) || 
                                (requiresBaseUrl && !formData.base_url) ||
                                (requiresCustomName && !formData.custom_name)
                            } 
                            className="btn btn-primary"
                        >
                            {testing ? 'Testing...' : 'Test & Add'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddLLMModal;

